import {useMemo, useState} from 'react';
import {AlertCircle, Braces, CheckCircle2, FileCode2, Search, Tag} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {useCompiledPrompts} from '@/api/hooks';
import {useDebounce} from '@/lib/useDebounce';
import type {CompiledPrompt} from '@/api/compiledPrompts';

function formatPrompt(prompt: CompiledPrompt['prompt']): string {
  if (typeof prompt === 'string') {
    return prompt;
  }

  return prompt
    .map((message) => {
      const content =
        typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
      return `${message.role ?? 'message'}\n${content ?? ''}`;
    })
    .join('\n\n');
}

export function PromptsAdmin() {
  const {data: prompts = [], isLoading, isError} = useCompiledPrompts();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const filteredPrompts = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) {
      return prompts;
    }

    return prompts.filter(
      (prompt) =>
        prompt.name.toLowerCase().includes(query) ||
        prompt.labels.some((label) => label.toLowerCase().includes(query))
    );
  }, [prompts, debouncedSearch]);

  if (isLoading) {
    return (
      <div
        className="text-muted-foreground flex min-h-48 items-center justify-center gap-2 text-xs"
        role="status">
        <Spinner /> Loading compiled prompts…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertTitle>Compiled prompts unavailable</AlertTitle>
        <AlertDescription>
          Run the Langfuse prompt fetch step before starting the API.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Alert>
        <CheckCircle2 />
        <AlertTitle>Langfuse is the source of truth</AlertTitle>
        <AlertDescription>
          These prompts were fetched from Langfuse and compiled into this build. This view is
          read-only; update prompts in Langfuse, then rebuild to publish a new version.
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium">Compiled prompts</h3>
          <p className="text-muted-foreground text-xs">
            {prompts.length} {prompts.length === 1 ? 'prompt' : 'prompts'} loaded in this build
          </p>
        </div>
        <InputGroup className="w-56 max-w-full">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            aria-label="Search compiled prompts"
            placeholder="Search prompts or labels"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </InputGroup>
      </div>

      {filteredPrompts.length === 0 ? (
        <Empty className="border" role="status">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileCode2 />
            </EmptyMedia>
            <EmptyTitle>No prompts match this search</EmptyTitle>
            <EmptyDescription>Try a different name or label.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredPrompts.map((prompt) => (
            <Card key={`${prompt.name}@${prompt.version}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCode2 className="text-muted-foreground size-4 shrink-0" />
                  <span className="truncate font-mono">{prompt.name}</span>
                </CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline">v{prompt.version}</Badge>
                  {prompt.labels.map((label) => (
                    <Badge key={label} variant="outline">
                      <Tag /> {label}
                    </Badge>
                  ))}
                </CardDescription>
                <CardAction>
                  <Badge variant="outline">Compiled</Badge>
                </CardAction>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <pre className="bg-card text-foreground max-h-80 overflow-auto rounded-md border p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap">
                  {formatPrompt(prompt.prompt)}
                </pre>
                <div className="text-muted-foreground flex flex-wrap gap-3 text-xs">
                  <span className="flex items-center gap-1">
                    <Braces className="size-3" />
                    {prompt.variables.length}{' '}
                    {prompt.variables.length === 1 ? 'variable' : 'variables'}
                  </span>
                  {prompt.config ? <span>Model config included</span> : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
